import type Echo from 'laravel-echo'

import { body, useApi } from '@/api/client'
import type { LiveUpdatesConfig } from '@/config'

/**
 * The live update socket (MRS 6.17): one connection to the Sockudo server
 * (Pusher protocol), opened on the first subscription. Echo and pusher-js load
 * then too, so a page without live updates never downloads them. A signal carries no
 * data; the page that hears it reads the REST API again. Without a configured
 * server there is no socket and `subscribe` does nothing.
 */

type Unsubscribe = () => void

let settings: LiveUpdatesConfig | null = null
let echo: Promise<Echo<'pusher'>> | null = null

/** Called once from main.ts. Null keeps live updates off. */
export function configureLiveUpdates(config: LiveUpdatesConfig | null): void {
  settings = config
}

function connection(config: LiveUpdatesConfig): Promise<Echo<'pusher'>> {
  echo ??= open(config)
  return echo
}

async function open(config: LiveUpdatesConfig): Promise<Echo<'pusher'>> {
  const [{ default: Echo }, { default: Pusher }] = await Promise.all([
    import('laravel-echo'),
    import('pusher-js'),
  ])
  return new Echo({
    broadcaster: 'pusher',
    Pusher,
    key: config.key,
    cluster: '',
    wsHost: config.host,
    wsPort: 443,
    wssPort: 443,
    forceTLS: true,
    enabledTransports: ['ws', 'wss'],
    // The API signs the channel through the typed client, so the session
    // cookie and the CSRF header go with it as with any other call.
    authorizer: (channel: { name: string }) => ({
      authorize: (
        socketId: string,
        callback: (error: Error | null, data: { auth: string } | null) => void,
      ) => {
        useApi()
          .POST('/api/v1/broadcasting/auth', {
            body: { socket_id: socketId, channel_name: channel.name },
          })
          .then((result) => callback(null, body(result)))
          .catch((error: unknown) =>
            callback(error instanceof Error ? error : new Error(String(error)), null),
          )
      },
    }),
  })
}

/**
 * Calls `onSignal` for each event on a private channel, and again when the
 * socket comes back after a drop (signals sent while it was down are lost).
 * Returns the function that leaves the channel.
 */
export function subscribe(channel: string, event: string, onSignal: () => void): Unsubscribe {
  if (settings === null) {
    return () => {}
  }
  let left = false
  let leave: Unsubscribe = () => {}

  void connection(settings).then((live) => {
    if (left) {
      return
    }
    live.private(channel).listen(`.${event}`, onSignal)

    const pusher = live.connector.pusher
    let dropped = false
    const onState = ({ current }: { current: string }) => {
      if (current === 'connected' && dropped) {
        dropped = false
        onSignal()
      } else if (current === 'unavailable' || current === 'disconnected') {
        dropped = true
      }
    }
    pusher.connection.bind('state_change', onState)

    leave = () => {
      pusher.connection.unbind('state_change', onState)
      live.leave(channel)
    }
  })

  return () => {
    left = true
    leave()
  }
}

/** Only for tests: closes and forgets the socket. */
export function _resetLiveUpdates(): void {
  void echo?.then((live) => live.disconnect())
  echo = null
  settings = null
}
